import { useState, useEffect, useCallback } from "react";
import { useSession } from "@clerk/clerk-react";
import { RocketLaunch, Check, Eye } from "@phosphor-icons/react";
import { Button } from "components/ui/button";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { candidacyPhaseColumns } from "screens/candidacies/candidacyPhaseConfig";

interface UnreadCandidate {
  id: number;
  status: string;
  unitId: string;
  buildingId: string | null;
  createDate: string | null;
  personalDetails: { firstName: string; lastName: string; email: string } | null;
}

interface StatusGroup {
  status: string;
  candidates: UnreadCandidate[];
}

interface NieuweKandidatenBannerProps {
  onCandidateClick?: (candidateId: number) => void;
}

const SHORT_LABELS: Record<string, string> = {
  searchingAppointmentMoment: 'Afspraak zoeken',
  'awaiting-interested': 'Wacht op interesse',
  'awaiting-alternate-selection': 'Alt. datum selecteren',
  'awaiting-visit': 'Bezoek gepland',
};

const shortName = (c: UnreadCandidate): string => {
  if (!c.personalDetails) return 'Onbekend';
  const { firstName, lastName } = c.personalDetails;
  const parts = lastName.trim().split(' ');
  const shortened = parts.length > 1
    ? parts[0] + ' ' + parts.slice(1).map(p => p[0] + '.').join(' ')
    : lastName;
  return `${firstName} ${shortened}`;
};

const dotColor = (colorClass: string) =>
  colorClass.replace('text-', 'bg-').replace('-700', '-400');

export const NieuweKandidatenBanner = ({ onCandidateClick }: NieuweKandidatenBannerProps) => {
  const { session } = useSession();
  const [interested, setInterested] = useState<UnreadCandidate[]>([]);
  const [others, setOthers] = useState<StatusGroup[]>([]);
  const [markingIds, setMarkingIds] = useState<Set<number>>(new Set());
  const [markingStatuses, setMarkingStatuses] = useState<Set<string>>(new Set());
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const fetchUnread = useCallback(async () => {
    if (!session) return;
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/unread-candidates`,
        { method: "GET" },
        session
      );
      if (response.ok) {
        const data = await response.json();
        setInterested(data.interested || []);
        setOthers(data.others || []);
      }
    } catch {
      // Banner is non-critical, fail silently
    }
  }, [session]);

  useEffect(() => { fetchUnread(); }, [fetchUnread]);

  const markAsRead = useCallback(async (ids: number[]) => {
    if (!session) return;
    try {
      await fetchWithAuth(
        `${API_URL}/rental-appointment/mark-as-read`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ applicationIds: ids }),
        },
        session
      );
      const idSet = new Set(ids);
      setInterested(prev => prev.filter(c => !idSet.has(c.id)));
      setOthers(prev =>
        prev
          .map(g => ({ ...g, candidates: g.candidates.filter(c => !idSet.has(c.id)) }))
          .filter(g => g.candidates.length > 0)
      );
    } catch {
      // Fail silently
    }
  }, [session]);

  const handleMarkOne = async (id: number) => {
    setMarkingIds(prev => new Set(prev).add(id));
    await markAsRead([id]);
    setMarkingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
  };

  const handleMarkGroup = async (statusKey: string, ids: number[]) => {
    setMarkingStatuses(prev => new Set(prev).add(statusKey));
    await markAsRead(ids);
    setMarkingStatuses(prev => { const s = new Set(prev); s.delete(statusKey); return s; });
  };

  const handleMarkAll = async () => {
    setIsMarkingAll(true);
    const allIds = [
      ...interested.map(c => c.id),
      ...others.flatMap(g => g.candidates.map(c => c.id)),
    ];
    await markAsRead(allIds);
    setIsMarkingAll(false);
  };

  const otherCount = others.reduce((acc, g) => acc + g.candidates.length, 0);
  if (interested.length === 0 && otherCount === 0) return null;

  return (
    <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50 overflow-hidden">

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-orange-200 bg-orange-100/60">
        <div className="flex items-center gap-2">
          <RocketLaunch weight="duotone" size={18} className="text-orange-700" />
          <span className="text-sm font-semibold text-orange-800">
            {interested.length === 1 ? "1 nieuwe geïnteresseerde kandidaat" : `${interested.length} nieuwe geïnteresseerde kandidaten`}
            {otherCount > 0 && (
              <span className="font-normal text-orange-600 ml-1">· {otherCount} andere</span>
            )}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-orange-700 hover:text-orange-900 hover:bg-orange-200/60 h-7 px-2 text-xs gap-1"
          onClick={handleMarkAll}
          disabled={isMarkingAll}
        >
          <Eye size={14} weight="bold" />
          Alles gelezen
        </Button>
      </div>

      {/* Geïnteresseerd — volledige rijen */}
      {interested.length > 0 && (
        <ul className="divide-y divide-orange-100">
          {interested.map(candidate => {
            const name = candidate.personalDetails
              ? `${candidate.personalDetails.firstName} ${candidate.personalDetails.lastName}`
              : "Onbekende kandidaat";
            const date = candidate.createDate
              ? format(new Date(candidate.createDate), "d MMM yyyy", { locale: nl })
              : "";
            return (
              <li key={candidate.id} className="flex items-center justify-between px-4 py-2.5 gap-3 hover:bg-orange-100/40 transition-colors">
                <button
                  className="flex-1 min-w-0 text-left"
                  onClick={() => onCandidateClick?.(candidate.id)}
                >
                  <span className="text-sm font-medium text-neutral-primary truncate block">{name}</span>
                  <span className="text-xs text-neutral-secondary-mid">
                    {candidate.unitId}
                    {candidate.buildingId ? ` · ${candidate.buildingId}` : ""}
                    {date ? ` · ${date}` : ""}
                  </span>
                </button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2.5 text-xs border-orange-300 text-orange-700 hover:bg-orange-200 hover:text-orange-900 bg-white shrink-0 gap-1"
                  onClick={() => handleMarkOne(candidate.id)}
                  disabled={markingIds.has(candidate.id)}
                >
                  <Check size={12} weight="bold" />
                  Gelezen
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Andere statussen — compacte rijen */}
      {others.length > 0 && (
        <ul className={`divide-y divide-orange-100/60 bg-white/30 ${interested.length > 0 ? "border-t border-orange-200/50" : ""}`}>
          {others.map(group => {
            const config = candidacyPhaseColumns.find(col => col.id === group.status);
            const textColor = config?.color ?? 'text-neutral-primary';
            const dot = dotColor(textColor);
            const label = SHORT_LABELS[group.status] ?? group.status;
            const names = group.candidates.map(shortName).join(' · ');
            const allIds = group.candidates.map(c => c.id);

            return (
              <li key={group.status} className="flex items-center justify-between px-4 py-1.5 gap-3">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
                  <span className={`text-xs font-medium shrink-0 ${textColor}`}>
                    {label}
                  </span>
                  <span className="text-xs text-neutral-secondary-mid">
                    ({group.candidates.length})
                  </span>
                  <span className="text-xs text-neutral-secondary-mid truncate">
                    {names}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs text-neutral-secondary-mid hover:text-neutral-primary shrink-0 gap-1"
                  onClick={() => handleMarkGroup(group.status, allIds)}
                  disabled={markingStatuses.has(group.status)}
                >
                  <Check size={10} weight="bold" />
                  Gelezen
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
